import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Building2, Camera, Mail, Pencil, Phone, Save, ShieldCheck, User } from "lucide-react";

import { AuthContext } from "../../Moduels/Context/AuthContext";
import { updateMyProfile } from "../../Moduels/auth/auth";
import { formatRoleLabel, isAdminLike } from "../../rbac/access";
import { normalizeApiError } from "../../utils/normalizeApiError";
import { notifyOrganisationBrandingUpdated } from "../../utils/orgBranding";
import { ToastStack, useToastStack } from "../ui/ToastStack";
import UserAvatar from "../user/UserAvatar";
import { loadProfileImage, saveProfileImage } from "../user/profileImageStore";
import "../../styles/accountProfile.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[0-9+\-().\s]{6,30}$/;

function getRole(me, fallbackRole) {
  return (me?.role || me?.user?.role || fallbackRole || "employee").toLowerCase();
}

function getOrgName(me) {
  return me?.organisation?.name || me?.user?.organisation?.name || "";
}

function getEmail(me) {
  return me?.user?.email || me?.employee?.email || me?.email || "";
}

function getPhone(me) {
  return me?.user?.phone || me?.employee?.phone || me?.employee?.mobile_phone || me?.phone || "";
}

function getName(me) {
  if (!me) return "";
  if (me.user) return me.user.full_name || me.user.username || "";
  if (me.employee) {
    const parts = [me.employee.first_name, me.employee.last_name].filter(Boolean);
    return parts.join(" ") || me.employee.display_name || "";
  }
  return me.full_name || "";
}

function readForm(me) {
  return {
    name: getName(me),
    email: getEmail(me),
    phone: getPhone(me),
    organisation: getOrgName(me),
  };
}

function ProfileField({
  id,
  label,
  icon: Icon,
  value,
  onChange,
  placeholder,
  locked,
  editing,
  canEdit,
  onToggle,
  hint,
  type = "text",
  autoComplete,
  inputRef,
  maxLength,
}) {
  return (
    <div className="acct-field">
      <label htmlFor={id}>{label}</label>
      <div className={`acct-control ${canEdit ? "has-pencil" : ""} ${locked ? "is-locked" : ""} ${editing ? "is-editing" : ""}`}>
        <Icon className="acct-control-icon" size={16} strokeWidth={1.75} aria-hidden />
        <input
          id={id}
          ref={inputRef}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          readOnly={locked}
          autoComplete={autoComplete}
          maxLength={maxLength}
          spellCheck={type === "email" ? false : undefined}
        />
        {canEdit ? (
          <button
            type="button"
            className={`acct-pencil ${editing ? "is-on" : ""}`}
            onClick={onToggle}
            aria-label={editing ? `Cancel ${label.toLowerCase()} edit` : `Edit ${label.toLowerCase()}`}
            aria-pressed={editing}
          >
            <Pencil size={14} strokeWidth={2} aria-hidden />
          </button>
        ) : null}
      </div>
      {hint ? <p className="acct-hint">{hint}</p> : null}
    </div>
  );
}

export default function ProfilePage() {
  const { user: me, role: authRole, applySessionProfile } = useContext(AuthContext);
  const { toasts, addToast, removeToast } = useToastStack();

  const role = useMemo(() => getRole(me, authRole), [me, authRole]);
  const isUserAccount = me?.principal_type === "user" || Boolean(me?.user);
  const canEditAccount = isUserAccount && isAdminLike(role);

  const [saving, setSaving] = useState(false);
  const [imageSrc, setImageSrc] = useState(() => loadProfileImage(me));
  const [imageDraft, setImageDraft] = useState(null);
  const [form, setForm] = useState(() => readForm(me));
  const [baseline, setBaseline] = useState(() => readForm(me));
  const [editing, setEditing] = useState({ email: false, phone: false, organisation: false });

  const fileRef = useRef(null);
  const emailRef = useRef(null);
  const phoneRef = useRef(null);
  const orgRef = useRef(null);

  useEffect(() => {
    const next = readForm(me);
    setForm(next);
    setBaseline(next);
    setEditing({ email: false, phone: false, organisation: false });
    setImageSrc(loadProfileImage(me));
    setImageDraft(null);
  }, [me]);

  const avatarSrc = imageDraft || imageSrc;
  const roleLabel = formatRoleLabel(role);

  const emailChanged =
    canEditAccount && form.email.trim().toLowerCase() !== baseline.email.trim().toLowerCase();
  const phoneChanged = form.phone.trim() !== baseline.phone.trim();
  const nameChanged = form.name.trim() !== baseline.name.trim();
  const orgChanged = canEditAccount && form.organisation.trim() !== baseline.organisation.trim();
  const dirty = nameChanged || emailChanged || phoneChanged || orgChanged || Boolean(imageDraft);

  const toggleField = (key) => {
    setEditing((prev) => {
      const opening = !prev[key];
      if (!opening) {
        setForm((current) => ({ ...current, [key]: baseline[key] }));
      } else {
        const ref = key === "email" ? emailRef : key === "phone" ? phoneRef : orgRef;
        requestAnimationFrame(() => ref.current?.focus());
      }
      return { ...prev, [key]: opening };
    });
  };

  const onPickImage = async (file) => {
    if (!file) return;
    if (!String(file.type || "").startsWith("image/")) {
      addToast("Please choose an image file", "error");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      addToast("Image must be under 3MB", "error");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : null;
      setImageDraft(result);
    };
    reader.onerror = () => addToast("Failed to read image", "error");
    reader.readAsDataURL(file);
  };

  const discard = () => {
    setForm(baseline);
    setImageDraft(null);
    setEditing({ email: false, phone: false, organisation: false });
  };

  const save = async () => {
    const name = form.name.trim();
    const email = form.email.trim();
    const phone = form.phone.trim();
    const organisation = form.organisation.trim();

    if (!name) {
      addToast("Name is required", "error");
      return;
    }
    if (emailChanged && !EMAIL_RE.test(email)) {
      addToast("Enter a valid email address", "error");
      return;
    }
    if (phone && !PHONE_RE.test(phone)) {
      addToast("Enter a valid phone number", "error");
      return;
    }
    if (orgChanged && organisation.length < 2) {
      addToast("Organisation name must be at least 2 characters", "error");
      return;
    }
    if (!dirty) {
      addToast("No changes to save", "error");
      return;
    }

    const payload = {};
    if (nameChanged) payload.full_name = name;
    if (phoneChanged) payload.phone = phone;
    if (emailChanged) payload.email = email;
    if (orgChanged) payload.organisation_name = organisation;

    try {
      setSaving(true);
      let profile = me;
      if (Object.keys(payload).length) {
        profile = await updateMyProfile(payload);
      }
      if (imageDraft) {
        saveProfileImage(profile || me, imageDraft);
      }
      if (profile && profile !== me) {
        applySessionProfile?.(profile);
      } else if (imageDraft) {
        setImageSrc(imageDraft);
        setImageDraft(null);
      }
      if (orgChanged) notifyOrganisationBrandingUpdated({ name: organisation });

      if (emailChanged) {
        addToast(`Profile saved. Sign in with ${email.toLowerCase()} from now on.`, "success");
      } else {
        addToast("Profile updated successfully", "success");
      }
    } catch (err) {
      addToast(normalizeApiError(err), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="acct-page">
      <ToastStack toasts={toasts} onRemove={removeToast} />

      <header className="acct-head">
        <p className="acct-kicker">Account</p>
        <h1>My profile</h1>
        <p className="acct-lead">
          {canEditAccount
            ? "Update your name, phone, organisation, and the email you use to sign in."
            : "Update your name and phone. Your sign-in email is managed by the organisation super admin."}
        </p>
      </header>

      <div className="acct-layout">
        <aside className="acct-identity">
          <div className="acct-band" aria-hidden />
          <div className="acct-identity-body">
            <div className="acct-avatar-wrap">
              <UserAvatar
                className="acct-avatar"
                name={form.name || "User"}
                src={avatarSrc}
                size={92}
                roundedClassName="rounded-full"
                initialsClassName="text-2xl"
              />
              <button
                type="button"
                className="acct-camera"
                onClick={() => fileRef.current?.click?.()}
                aria-label="Upload profile photo"
              >
                <Camera size={15} strokeWidth={2} aria-hidden />
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onPickImage(e.target.files?.[0] || null)}
              />
            </div>

            <p className="acct-identity-name">{form.name || "—"}</p>
            <p className="acct-identity-email">{form.email || "No email"}</p>
            <div className="acct-role">
              <ShieldCheck size={13} aria-hidden />
              {roleLabel}
            </div>

            <ul className="acct-meta">
              <li>
                <span>Organisation</span>
                <strong>{form.organisation || "—"}</strong>
              </li>
              <li>
                <span>Phone</span>
                <strong>{form.phone || "Not added"}</strong>
              </li>
            </ul>

            {imageDraft ? (
              <div className="acct-photo-note">
                <span>New photo ready</span>
                <button type="button" className="acct-text-btn" onClick={() => setImageDraft(null)} disabled={saving}>
                  Remove
                </button>
              </div>
            ) : null}
          </div>
        </aside>

        <section className="acct-panel">
          <header className="acct-panel-head">
            <h2>Profile details</h2>
            <p>
              {canEditAccount
                ? "Use the pencil to edit email, phone, or organisation. Saving an email replaces the old one straight away."
                : "You can update your name and phone. Email and organisation are managed by your super admin."}
            </p>
          </header>

          <div className="acct-fields">
            <ProfileField
              id="profile-name"
              label="Name"
              icon={User}
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Full name"
              autoComplete="name"
              maxLength={150}
            />

            <ProfileField
              id="profile-email"
              label="Email"
              icon={Mail}
              type="email"
              value={form.email}
              onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
              placeholder="name@company.com"
              autoComplete="email"
              locked={!editing.email}
              editing={editing.email}
              canEdit={canEditAccount}
              onToggle={() => toggleField("email")}
              inputRef={emailRef}
              maxLength={255}
              hint={
                canEditAccount
                  ? editing.email
                    ? "This replaces your current sign-in email as soon as you save."
                    : "Click the pencil to change the email you sign in with."
                  : "Email is managed by your organisation admin."
              }
            />

            <ProfileField
              id="profile-phone"
              label="Phone"
              icon={Phone}
              type="tel"
              value={form.phone}
              onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
              placeholder="Phone number"
              autoComplete="tel"
              locked={!editing.phone}
              editing={editing.phone}
              canEdit
              onToggle={() => toggleField("phone")}
              inputRef={phoneRef}
              maxLength={30}
              hint={editing.phone ? "Include country code if you use one." : "Click the pencil to update your phone number."}
            />

            <ProfileField
              id="profile-organisation"
              label="Organisation"
              icon={Building2}
              value={form.organisation}
              onChange={(e) => setForm((prev) => ({ ...prev, organisation: e.target.value }))}
              placeholder="Organisation name"
              locked={!editing.organisation}
              editing={editing.organisation}
              canEdit={canEditAccount}
              onToggle={() => toggleField("organisation")}
              inputRef={orgRef}
              maxLength={200}
              hint={
                canEditAccount
                  ? editing.organisation
                    ? "This name is shown across the workspace after you save."
                    : "Click the pencil to rename the organisation."
                  : "Organisation name is managed by your super admin."
              }
            />
          </div>

          {emailChanged ? (
            <div className="acct-note" role="status">
              <Mail size={16} aria-hidden />
              <p>
                Saving replaces <strong>{baseline.email}</strong> with <strong>{form.email.trim()}</strong>.
                The new address works for sign-in immediately. This session stays signed in.
              </p>
            </div>
          ) : null}

          <div className="acct-footer">
            {dirty ? (
              <button type="button" className="acct-btn acct-btn-ghost" onClick={discard} disabled={saving}>
                Discard
              </button>
            ) : null}
            <button type="button" className="acct-btn acct-btn-primary" onClick={save} disabled={saving || !dirty}>
              <Save size={16} aria-hidden />
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
